import { useCallback, useEffect, useRef, useState } from 'react';
import { TransitionPhase } from './types';

export interface UseSceneTransitionOptions {
  /** Total duration ms — split 50/50 between exit and enter. Default 600. */
  durationMs?: number;
  /** Disable transitions entirely (phase will always be 'idle'). */
  disabled?: boolean;
}

export interface UseSceneTransitionResult {
  phase: TransitionPhase;
  /** Wrap a navigation callback; plays exit, calls navigate at midpoint, plays enter. */
  wrapNavigate: (navigate: () => void) => void;
  /** Force-cancel any in-flight transition without running the pending action. */
  cancel: () => void;
  /** True iff a transition is currently playing. */
  isAnimating: boolean;
}

export function useSceneTransition(options: UseSceneTransitionOptions = {}): UseSceneTransitionResult {
  const { durationMs = 600, disabled = false } = options;
  const half = Math.max(0, durationMs / 2);

  const [phase, setPhase] = useState<TransitionPhase>('idle');
  const phaseRef = useRef<TransitionPhase>('idle');
  const pendingAction = useRef<(() => void) | null>(null);
  phaseRef.current = phase;

  const wrapNavigate = useCallback((navigate: () => void) => {
    if (disabled) {
      navigate();
      return;
    }
    if (phaseRef.current !== 'idle') return;
    pendingAction.current = navigate;
    setPhase('exit');
  }, [disabled]);

  const cancel = useCallback(() => {
    pendingAction.current = null;
    setPhase('idle');
  }, []);

  useEffect(() => {
    if (phase === 'exit') {
      const timer = setTimeout(() => {
        pendingAction.current?.();
        pendingAction.current = null;
        setPhase('enter');
      }, half);
      return () => clearTimeout(timer);
    }
    if (phase === 'enter') {
      const timer = setTimeout(() => setPhase('idle'), half);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [phase, half]);

  return {
    phase,
    wrapNavigate,
    cancel,
    isAnimating: phase !== 'idle',
  };
}
